<template>
    <div id="app" class="site container hfeed">
        <HeadNav selectedLanguage="en" page="features" />

        <div class="markdown-content">
            <h1>GB WhatsApp Features Overview</h1>

            <p>GB WhatsApp is a powerful modified version of the official WhatsApp, offering enhanced features that cater to users who want more control and customization. This comprehensive guide explores all the key features that make GB WhatsApp stand out from the official application.</p>

            <h2>Introduction to GB WhatsApp</h2>

            <p>GB WhatsApp has gained significant popularity among Android users worldwide due to its extensive feature set and customization options. Unlike the official WhatsApp, GB WhatsApp provides users with advanced controls over their messaging experience, privacy settings, and media sharing capabilities.</p>

            <h2>Messaging Enhancements</h2>

            <p>One of the most appealing aspects of GB WhatsApp is its enhanced messaging features:</p>
            <ul>
                <li><strong>Extended Status Duration:</strong> Post status updates up to 60 seconds instead of the standard 30 seconds</li>
                <li><strong>Auto-Reply:</strong> Set up automatic responses when you're busy</li>
                <li><strong>Message Scheduler:</strong> Schedule messages to be sent at specific times</li>
                <li><strong>Bulk Messaging:</strong> Send messages to multiple contacts simultaneously</li>
                <li><strong>Message Translation:</strong> Translate messages directly within the chat</li>
            </ul>

            <h2>Media Sharing Features</h2>

            <p>GB WhatsApp offers superior media sharing capabilities:</p>
            <ul>
                <li><strong>High-Quality Media:</strong> Send images without compression</li>
                <li><strong>Extended Video Size:</strong> Share videos up to 50MB</li>
                <li><strong>Custom Media Download:</strong> Choose which media to download</li>
                <li><strong>Media Gallery Lock:</strong> Hide media from gallery view</li>
            </ul>

            <h2>Privacy & Security Controls</h2>

            <p>Privacy is a major concern for many users, and GB WhatsApp addresses this with comprehensive privacy features:</p>
            <ul>
                <li><strong>Hide Online Status:</strong> Appear offline while chatting</li>
                <li><strong>Blue Ticks Control:</strong> Choose when to show read receipts</li>
                <li><strong>Anti-Delete Messages:</strong> Prevent others from deleting messages</li>
                <li><strong>Privacy Lock:</strong> Lock chats with PIN or fingerprint</li>
                <li><strong>Freeze Last Seen:</strong> Set a fake last seen time</li>
            </ul>

            <h2>Multi-Account Management</h2>

            <p>For users with multiple phone numbers, GB WhatsApp provides excellent multi-account support:</p>
            <ul>
                <li><strong>Dual Account:</strong> Run two WhatsApp accounts simultaneously</li>
                <li><strong>Separate Notifications:</strong> Distinct notification sounds for each account</li>
                <li><strong>Data Isolation:</strong> Complete separation between accounts</li>
            </ul>

            <h2>Chat Customization Options</h2>

            <p>Personalize your chat experience with various customization options:</p>
            <ul>
                <li><strong>Custom Themes:</strong> Choose from hundreds of themes</li>
                <li><strong>Font Styles:</strong> Change chat fonts</li>
                <li><strong>Chat Bubbles:</strong> Customize conversation bubbles</li>
                <li><strong>Wallpapers:</strong> Set custom chat wallpapers</li>
            </ul>

            <h2>Notification Management</h2>

            <p>Stay organized with advanced notification controls:</p>
            <ul>
                <li><strong>Custom Notifications:</strong> Set unique sounds per contact</li>
                <li><strong>DND Mode:</strong> Do Not Disturb functionality</li>
                <li><strong>Message Counter:</strong> Show unread message count in notification</li>
            </ul>

            <h2>Auto-Reply Features</h2>

            <p>Perfect for business users, the auto-reply feature allows you to:</p>
            <ul>
                <li>Set up automated responses for specific contacts</li>
                <li>Create custom auto-reply messages</li>
                <li>Schedule auto-reply activation times</li>
            </ul>

            <h2>Compatibility & System Requirements</h2>

            <p>GB WhatsApp is designed to work on a wide range of Android devices:</p>
            <ul>
                <li><strong>Android Version:</strong> Requires Android 4.0.3 or higher</li>
                <li><strong>Storage:</strong> Needs approximately 50MB of free space</li>
                <li><strong>Regular Updates:</strong> Stay updated with the latest features</li>
            </ul>

            <h2>Related Features</h2>

            <p>Explore more specific features in these detailed guides:</p>
            <ul>
                <li><a href="/features/privacy/">Privacy & Security Features</a> - Learn about all privacy controls</li>
                <li><a href="/features/media/">Media Sharing Features</a> - Master media sharing capabilities</li>
                <li><a href="/use-cases/business/">Business Use Cases</a> - How businesses benefit from GB WhatsApp</li>
            </ul>
        </div>
    </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/index.scss';
import HeadNav from '@/components/HeadNav.vue';

export default {
    name: 'Features',
    components: { HeadNav },
};
</script>

<style scoped>
.markdown-content {
    padding: 20px;
    line-height: 1.6;
    margin: 0 auto;
    max-width: 1000px;
    background: #ffffff;
    border-radius: 8px;
}

.markdown-content h1 {
    font-size: 2em;
    margin: 0.67em 0;
    border-bottom: 2px solid #eee;
    padding-bottom: 0.3em;
    color: #2d3748;
    text-align: center;
}

.markdown-content h2 {
    font-size: 1.5em;
    margin: 0.75em 0;
    border-bottom: 1px solid #eee;
    padding-bottom: 0.3em;
    color: #2d3748;
    margin-top: 40px;
}

.markdown-content h3 {
    font-size: 1.25em;
    margin: 0.83em 0;
    color: #2d3748;
    margin-top: 30px;
}

.markdown-content p {
    margin: 1em 0;
}

.markdown-content ul,
.markdown-content ol {
    margin: 1em 0;
    padding-left: 2em;
}

.markdown-content li {
    margin: 0.5em 0;
}

.markdown-content img {
    width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 1em 0;
}

.markdown-content a {
    color: #667eea;
    text-decoration: none;
}

.markdown-content a:hover {
    text-decoration: underline;
}

.markdown-content strong {
    font-weight: 700;
}
</style>
